// @ts-check
import { QUANTUM_LEVELS } from '../content/levels/quantum.js';
import { CAMPAIGN, WORLDS } from '../content/campaign.js';
import { createSession } from '../game/session.js';
import { createInput, isAction } from '../platform/input.js';
import { createLoop } from '../platform/loop.js';
import { createLifetime } from '../platform/lifetime.js';
import { observeImage } from '../platform/assets.js';
import { createScene } from '../rendering/scene.js';
import { getGameElements } from '../ui/dom.js';
import { walkFocus } from '../ui/focus-walk.js';
import { createHud } from '../ui/hud.js';
import { createCampaignPanel } from '../ui/campaign.js';
import { createProgressStore } from '../platform/progress-store.js';
import { readProgress } from '../game/progress.js';
import { createAudio } from '../platform/audio.js';
import { preferences } from '../platform/preferences.js';

/** Mount one game. Calling dispose releases every listener, frame, image callback, and observer.
 * @param {{initialProgress?:unknown,root?:Document,definition?:import('../game/types.js').LevelDefinition,nextLevels?:readonly import('../game/types.js').LevelDefinition[]}} [options]
 */
export function createGame({ initialProgress, root = document, definition = CAMPAIGN[0], nextLevels = definition === CAMPAIGN[0] ? CAMPAIGN.slice(1) : [] } = {}) {
  const view = getGameElements(root);
  const menu = root.querySelector('#game-menu');
  const life = createLifetime();
  const mode = new URL(window.location.href).searchParams.get('mode') === 'free' ? 'free' : 'level';
  const storageNotice = root.createElement('p'); storageNotice.id = 'save-notice'; storageNotice.setAttribute('role', 'status'); root.body.append(storageNotice); life.add(() => storageNotice.remove());
  const persistent = definition === CAMPAIGN[0];
  const saves = createProgressStore(raw => readProgress(raw, [definition, ...nextLevels], QUANTUM_LEVELS), message => { storageNotice.textContent = message; });
  const progress = persistent ? saves.load() : readProgress(initialProgress, [definition, ...nextLevels], QUANTUM_LEVELS);
  const session = createSession({ definition, nextLevels, secretLevels: QUANTUM_LEVELS, mode, progress, now: () => performance.now(), assist: preferences.assist });
  const audio = createAudio();
  const sound = root.createElement('button'); sound.type = 'button'; sound.id = 'sound-toggle'; sound.textContent = 'Sound off'; sound.setAttribute('aria-pressed', 'false'); root.querySelector('.scene-nav nav')?.append(sound);
  life.listen(sound, 'click', () => { void audio.toggle().then(() => { sound.textContent = audio.enabled ? 'Sound on' : 'Sound off'; sound.setAttribute('aria-pressed', String(audio.enabled)); }); });
  life.add(() => { audio.dispose(); sound.remove(); });
  // Assist, like Nintendo's assist modes: the same world, with falls and base hits forgiven.
  const assist = root.createElement('button'); assist.type = 'button'; assist.id = 'assist-toggle';
  assist.title = 'Assist: a fall returns QP to solid ground, and a hit without an upgrade knocks QP back instead of ending the run';
  const showAssist = () => { assist.textContent = session.assist ? 'Assist on' : 'Assist off'; assist.setAttribute('aria-pressed', String(session.assist)); };
  showAssist(); root.querySelector('.scene-nav nav')?.append(assist);
  life.listen(assist, 'click', () => { session.assist = !session.assist; preferences.assist = session.assist; showAssist(); offerAssist(); });
  life.add(() => assist.remove());
  // Like Nintendo's Super Guide block: help is offered after a streak of defeats, never forced.
  let defeats = 0;
  const assistResult = root.createElement('button'); assistResult.type = 'button'; assistResult.id = 'assist-result';
  assistResult.textContent = 'Reboot with Assist'; assistResult.hidden = true;
  view.result.append(assistResult);
  const offerAssist = () => { assistResult.hidden = session.assist || defeats < 3 || session.level.status !== 'dead'; };
  life.add(() => assistResult.remove());
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let mounted = false;
  let artworkReady = view.image.complete && view.image.naturalWidth > 0;
  let artworkFailed = false;
  const loadStatus = root.getElementById('entrance-status');
  const reload = root.getElementById('reload-game');
  /** @type {import('../ui/hud.js').PlayFlow} */ let flow = 'ready';
  /** @type {import('../ui/hud.js').PlayFlow|null} */ let drawnFlow = null;
  const hud = createHud(view);
  const scene = createScene(view, () => { if (mounted) { draw(); wake(); } });
  life.add(() => hud.dispose());
  const campaign = createCampaignPanel(root, WORLDS, session, { open: pause, fresh() {
    input.clear(); loop.stop(); session.reset(true); flow = 'ready'; scene.resize(session); draw();
    if (persistent) saves.save(session.snapshot());
  }, quantum() {
    if (session.enterQuantumWorld()) { input.clear(); flow = 'transferring'; scene.resize(session); draw(); wake(); }
  }, select(id) {
    input.clear(); loop.stop();
    if (session.select(id)) { flow = 'ready'; scene.resize(session); draw(); if (persistent) saves.save(session.snapshot()); }
  } });
  life.add(() => campaign.dispose());
  const input = createInput(window, view.buttons, {
    now: () => performance.now(),
    enabled: canPlay,
    changed: () => { if (!canPlay()) input.clear(); else { scene.interruptIdle(); wake(); } draw(); },
    pressed: (action) => { if (canPlay()) session.command(action); },
    restart: () => { if (session.mode === 'level' && flow !== 'ready') restart(); },
    control(control) {
      if (life.disposed || root.hidden) return;
      if (control === 'start') { if (!back()) { if (flow === 'ready') resume(); else if (flow === 'ended') restart(); } return; }
      // A stray Select behind an open overlay must not discard the run the player is browsing away from.
      if (control === 'select') { if (session.mode === 'level' && flow !== 'ready' && !campaign.visible && !menu?.hasAttribute('open')) restart(); return; }
      if (control === 'confirm') {
        const active = root.activeElement;
        if (active instanceof HTMLElement && active !== root.body && active !== view.stage) active.click();
        else if (flow === 'ready' || flow === 'paused') resume();
        return;
      }
      const scope = campaign.visible ? root.getElementById('campaign-map') : menu?.hasAttribute('open') ? menu : view.guide.hasAttribute('open') ? view.guide : root.body;
      if (scope) walkFocus(scope, control === 'previous' ? -1 : 1);
    },
  }, { gamepads: () => window.navigator.getGamepads?.() ?? [], request: (callback) => requestAnimationFrame(callback), cancel: (id) => cancelAnimationFrame(id) });
  const loop = createLoop({
    now: () => performance.now(), request: (callback) => requestAnimationFrame(callback), cancel: (id) => cancelAnimationFrame(id),
    step(dt) {
      const oldX = session.player.x, wasAirborne = !session.player.grounded;
      session.step(input.sample(), dt);
      if (session.mode === 'level' && session.level.started && session.level.status === 'playing' && !session.transfer) audio.step(dt, session.level.definition.world ?? 1, !!session.level.boss && session.level.boss.phase !== 'dormant');
      hud.step(dt);
      scene.step(session, dt, session.player.x - oldX, wasAirborne);
    },
    render: () => draw(),
    // A first RAF can precede the first fixed step. Keep pending input alive
    // until the simulation consumes it, including on 144/240 Hz displays.
    active: () => canRun() && (session.active || scene.active || hud.active || input.hasTimedActions || input.held('left') || input.held('right')),
  });
  life.add(session.events.subscribe(scene.accept)); life.add(session.events.subscribe(hud.accept));
  life.add(session.events.subscribe(audio.accept));
  life.add(session.events.subscribe((event) => {
    if (event.type === 'failed' || event.type === 'completed') { flow = 'ended'; input.clear(); }
    if (event.type === 'failed') defeats++;
    if (['checkpoint', 'completed', 'level-entered'].includes(event.type)) defeats = 0;
    offerAssist();
    if (event.type === 'uplink-entered') { flow = 'transferring'; input.clear(); }
    if (event.type === 'arrived') { flow = 'playing'; input.clear(); }
    if (persistent && session.mode === 'level' && ['transistor-found', 'checkpoint', 'uplink-entered', 'level-entered', 'completed', 'failed'].includes(event.type)) saves.save(session.snapshot());
  }));
  life.add(() => session.dispose()); life.add(() => scene.dispose()); life.add(() => input.dispose()); life.add(() => loop.dispose());
  /** @param {string} action */
  const held = (action) => isAction(action) && input.held(action);
  function canPlay() { return flow === 'playing' && !menu?.hasAttribute('open') && !campaign.visible && !view.guide.hasAttribute('open') && !root.hidden && !life.disposed; }
  function canRun() { return (flow === 'playing' || flow === 'transferring') && !menu?.hasAttribute('open') && !campaign.visible && !view.guide.hasAttribute('open') && !root.hidden && !life.disposed; }
  function draw() {
    if (life.disposed) return;
    const engaged = input.held('left') || input.held('right') || input.held('jump') || input.held('down') || input.held('pulse') || input.held('sprint');
    hud.render(session, scene.render(session, reduced.matches, input.held('sprint'), flow, root.hidden || campaign.visible || view.guide.hasAttribute('open') || !!menu?.hasAttribute('open'), engaged), held);
    hud.renderFlow(flow);
    view.flow.dataset.assets = artworkFailed ? 'error' : artworkReady ? 'ready' : 'loading';
    view.start.setAttribute('aria-disabled', String(!artworkReady));
    if (loadStatus) {
      const message = artworkFailed ? 'QP could not load. Reload to try again. Your saved progress is safe.' : artworkReady ? 'QP ready. Your next adventure is waiting.' : 'Loading QP artwork…';
      if (loadStatus.textContent !== message) loadStatus.textContent = message;
      loadStatus.hidden = flow !== 'ready' && !artworkFailed;
    }
    if (reload) reload.hidden = !artworkFailed;
    if (flow !== drawnFlow) {
      drawnFlow = flow;
      if (!root.hidden && !view.guide.hasAttribute('open')) {
        if (flow === 'ready' || flow === 'paused') view.start.focus({ preventScroll: true });
        if (flow === 'ended') view.restartResult.focus({ preventScroll: true });
      }
    }
  }
  function wake() { if (canRun()) loop.wake(); }
  function resize() { scene.resize(session); draw(); }
  function pause() {
    if (flow !== 'playing' && flow !== 'transferring') return;
    flow = 'paused'; input.clear(); session.pause(); scene.pause(); audio.pause(); loop.stop(); draw();
    if (persistent && session.mode === 'level') saves.save(session.snapshot());
  }
  function resume() {
    if (life.disposed || !artworkReady || root.hidden || campaign.visible || view.guide.hasAttribute('open') || flow === 'ended') return;
    menu?.removeAttribute('open'); input.clear(); flow = session.transfer ? 'transferring' : 'playing'; draw(); view.stage.focus({ preventScroll: true }); wake();
  }
  /** A controller's Start: close the topmost overlay, or toggle pause. @returns {boolean} whether it acted */
  function back() {
    if (campaign.visible) { campaign.close(); return true; }
    if (menu?.hasAttribute('open')) { menu.removeAttribute('open'); return true; }
    if (view.guide.hasAttribute('open')) { view.guide.removeAttribute('open'); return true; }
    if (flow === 'playing' || flow === 'transferring') { pause(); return true; }
    if (flow === 'paused') { resume(); return true; }
    return false;
  }
  function restart() {
    if (life.disposed || root.hidden || view.guide.hasAttribute('open')) return;
    input.clear(); loop.stop(); session.reset(flow === 'ended' && session.level.status === 'won'); scene.resize(session);
    flow = 'ready';
    if (artworkReady) resume(); else draw();
    if (persistent && session.mode === 'level') saves.save(session.snapshot());
  }
  for (const button of view.modes) life.listen(button, 'click', () => {
    menu?.removeAttribute('open');
    view.guide.removeAttribute('open'); flow = 'ready';
    input.clear(); loop.stop(); session.setMode(button.dataset.mode === 'free' ? 'free' : 'level');
    draw(); resize(); session.reset(); draw(); view.start.focus({ preventScroll: true });
  });
  life.listen(view.guide, 'toggle', () => {
    if (view.guide.hasAttribute('open')) pause();
    draw();
    if (!view.guide.hasAttribute('open') && (flow === 'ready' || flow === 'paused')) view.start.focus({ preventScroll: true });
  });
  if (menu) life.listen(menu, 'toggle', () => { if (menu.hasAttribute('open')) pause(); });
  life.listen(view.start, 'click', resume); life.listen(view.pause, 'click', pause);
  life.listen(window, 'keydown', (raw) => {
    const event = /** @type {KeyboardEvent} */ (raw);
    if (event.code === 'Enter' && !event.repeat && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && flow === 'ready' && !menu?.hasAttribute('open')) {
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('button, summary, a, input, textarea, select, [contenteditable]')) return;
      event.preventDefault(); resume(); return;
    }
    if (event.code !== 'Escape' || event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
    if (menu?.hasAttribute('open')) { event.preventDefault(); menu.removeAttribute('open'); return; }
    if (view.guide.hasAttribute('open')) { view.guide.removeAttribute('open'); return; }
    if (flow === 'playing' || flow === 'transferring') { event.preventDefault(); pause(); }
    else if (flow === 'paused') { event.preventDefault(); resume(); }
  });
  life.listen(view.restart, 'click', restart); life.listen(view.restartResult, 'click', restart);
  life.listen(assistResult, 'click', () => { session.assist = true; preferences.assist = true; showAssist(); restart(); offerAssist(); });
  life.listen(window, 'blur', pause); life.listen(window, 'pagehide', pause);
  life.listen(window, 'pageshow', draw); life.listen(window, 'focus', draw);
  life.listen(root, 'visibilitychange', () => { if (root.hidden) pause(); draw(); });
  life.listen(reduced, 'change', draw); life.listen(window, 'resize', resize);
  const observer = new ResizeObserver(resize); observer.observe(view.stage); life.add(() => observer.disconnect());
  /** Stop play and present recovery when the currently displayed artwork fails. */
  const imageError = () => { artworkFailed = true; artworkReady = false; view.error.hidden = false; view.qp.hidden = true; view.shadow.hidden = true; if (mounted) { pause(); draw(); } };
  life.add(observeImage(view.image, () => {
    artworkReady = true; artworkFailed = false;
    view.error.hidden = true; view.qp.hidden = false; view.shadow.hidden = false;
    if (mounted) draw();
  }, imageError));
  if (reload) life.listen(reload, 'click', () => window.location.reload());
  // The fallback image can change direction. Its later errors still need a message.
  life.listen(view.image, 'error', imageError);
  mounted = true;
  draw(); resize(); session.reset();
  if (progress?.complete && mode === 'level') { session.level.status = 'won'; flow = 'ended'; }
  draw(); wake();
  return { session, start: resume, pause, resume, restart, dispose: () => life.dispose(), get timing() { return loop.stats; } };
}
